// Global Google Maps API loader utility
// This prevents multiple API loads across the application

declare global {
    interface Window {
        google: any;
        googleMapsLoaded: boolean;
        googleMapsLoading: boolean;
        googleMapsLoadPromise: Promise<void> | null;
    }
}

class GoogleMapsLoader {
    private static instance: GoogleMapsLoader;
    private loadPromise: Promise<void> | null = null;
    private apiKey: string;

    private constructor() {
        // Get API key from environment variable or use fallback
        this.apiKey = typeof process !== 'undefined' && process.env?.GOOGLE_MAPS_API_KEY
            ? process.env.GOOGLE_MAPS_API_KEY
            : 'AIzaSyCgKXbiMcPdFtpfG9Y_uKFylyM52CwMO1M';
    }

    public static getInstance(): GoogleMapsLoader {
        if (!GoogleMapsLoader.instance) {
            GoogleMapsLoader.instance = new GoogleMapsLoader();
        }
        return GoogleMapsLoader.instance;
    }

    public load(): Promise<void> {
        // If already loaded, return immediately
        if (window.google && window.googleMapsLoaded) {
            return Promise.resolve();
        }

        // If already loading, return the existing promise
        if (this.loadPromise) {
            return this.loadPromise;
        }

        // Check if there's a global promise from another instance
        if (window.googleMapsLoadPromise) {
            return window.googleMapsLoadPromise;
        }

        // Create new loading promise
        this.loadPromise = this.loadAPI();
        window.googleMapsLoadPromise = this.loadPromise;

        return this.loadPromise;
    }

    private loadAPI(): Promise<void> {
        return new Promise((resolve, reject) => {
            // Check if already loading
            if (window.googleMapsLoading) {
                // Wait for the existing loading to complete
                const checkLoaded = () => {
                    if (window.google && window.googleMapsLoaded) {
                        resolve();
                    } else if (window.googleMapsLoading) {
                        setTimeout(checkLoaded, 100);
                    } else {
                        reject(new Error('Failed to load Google Maps API'));
                    }
                };
                checkLoaded();
                return;
            }

            // Set loading flag
            window.googleMapsLoading = true;

            const script = document.createElement('script');
            script.src = `https://maps.googleapis.com/maps/api/js?key=${this.apiKey}&libraries=places`;
            script.async = true;
            script.defer = true;

            script.onload = () => {
                window.googleMapsLoaded = true;
                window.googleMapsLoading = false;
                this.loadPromise = null;
                window.googleMapsLoadPromise = null;
                resolve();
            };

            script.onerror = () => {
                window.googleMapsLoading = false;
                this.loadPromise = null;
                window.googleMapsLoadPromise = null;
                reject(new Error('Failed to load Google Maps API'));
            };

            document.head.appendChild(script);
        });
    }

    public isLoaded(): boolean {
        return !!(window.google && window.googleMapsLoaded);
    }

    public isLoading(): boolean {
        return !!window.googleMapsLoading;
    }

    public setApiKey(apiKey: string): void {
        this.apiKey = apiKey;
    }
}

export const googleMapsLoader = GoogleMapsLoader.getInstance();
